import { useState, useEffect, useMemo } from 'react';
import { F, mr, EASE, DUR } from '../styles/theme';
import { usePress } from '../components/red/Press';
import { useTheme } from '../context/ThemeContext';
import { useCurrency } from '../context/CurrencyContext';
import { useProfile } from '../context/ProfileContext';
import { useT } from '../i18n/useT';
import { fetchHistoricalData, clearHistoricalCache } from '../utils/coingecko';
import { fmt } from '../utils/format';
import { calcAssetStats, calcAssetInvestment } from '../utils/portfolioCalc';
import { fifo } from '../utils/fifo';
import { mergeTxsByDay } from '../utils/exportData';
import { buildTransferMap } from '../utils/transferMatcher';
import { buildTxView, scamDustTxIds, dropScamDustTxs, isCryptoWalletTransfer } from '../utils/txView';
import { buildTaxLots } from '../utils/taxLots';
import { rulesFor } from '../utils/taxRules';
import { TransferChainCard, buildGroupedChains, formatDateHeader } from '../components/TxCards';
import TxRow from '../components/TxRow';
import { motion, useReducedMotion } from 'framer-motion';
import { useHistoricalPrices } from '../hooks/useHistoricalPrices';
import { useBreakpoint } from '../hooks/useBreakpoint';
import CoinImage from '../components/CoinImage';

import { BigAreaChart } from '../components/red/Charts';
import { Icon, Icons } from '../components/red/Icons';
import TaxTimeline from '../components/red/TaxTimeline';
import ExportSheet from '../components/red/ExportSheet';
import AssetAnalyseSheet from '../components/red/AssetAnalyseSheet';
import { RedWordmark } from '../components/red/Brand';
import { Segmented } from '../components/red/Editorial';
import { useUserPlan } from '../context/UserPlanContext';
import { exportAssetReport } from '../utils/exportService';

// Parität zu WalletScreen: Fiat-only Transfers (EUR Deposit/Withdraw), NFT-
// Bewegungen und interne Trading-Fee-TXs werden auch in der Detail-Liste
// ausgeblendet. ActivityRow == WalletRow Display-Logik.
const AD_FIAT_SET = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);
function adIsNonAssetTransfer(tx) {
  if (tx.typ !== 'einzahlung' && tx.typ !== 'auszahlung') return false;
  const sym = String(tx.coin || tx.sym || '').toUpperCase();
  return AD_FIAT_SET.has(sym) || sym === 'NFT';
}
function adIsInternalFeeTx(tx) {
  return tx.typ === 'gebuehr';
}

const PERIODS = [
  { label: '1T', days: 1 },
  { label: '1W', days: 7 },
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '1J', days: 365 },
];

// ── Section-Headline (Calm: sentence-case Headline, keine Nummer/Caps-Kicker) ──
function SectionDivider({ label }) {
  const { T } = useTheme();
  return (
    <div style={{ padding: '30px 18px 12px' }}>
      <div style={{ fontFamily: F.display, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: T.text }}>
        {label}
      </div>
    </div>
  );
}

// ── Period pill ─────────────────────────────────────────────────────

// ── Chart wrapper ───────────────────────────────────────────────────
function ChartSection({ asset, txs }) {
  const { T } = useTheme();
  const { cgParam, locale, symbol } = useCurrency();
  const { t } = useT();
  const { isWide } = useBreakpoint();
  const retryPress = usePress({ scale: 0.96 });
  const [period, setPeriod] = useState(PERIODS[2]); // 1M default
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!asset.coinId) return;
    let cancelled = false;
    // Debounce: schnelle Pill-Klicks (1T → 1W → 1M → 3M) feuern nur einen Request am Ende.
    const tid = setTimeout(() => {
      setLoading(true);
      setError(false);
      fetchHistoricalData(asset.coinId, period.days, cgParam)
        .then(d => { if (!cancelled) setData(d); })
        .catch(() => { if (!cancelled) setError(true); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 220);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [asset.coinId, period.days, cgParam]);

  const prices = data?.map(p => p[1]) || [];
  const high = prices.length ? Math.max(...prices) : 0;
  const low = prices.length ? Math.min(...prices) : 0;
  // Variante B wie der Dashboard-Hero: richtungs-getönt, aber sanftes Rot/Grün
  // (T.gain/T.loss), NICHT kräftiges Marken-Rot.
  const lineColor = prices.length && prices[prices.length - 1] >= prices[0]
    ? (T.gain || T.green)
    : (T.loss || T.red);

  // Buy/Sell-Marker aus den TXs dieses Assets ableiten.
  // kauf/verkauf -> direkt; swap -> coinOut = Sell, coinIn = Buy.
  const markers = useMemo(() => {
    if (!txs || !txs.length) return [];
    const sym = String(asset.sym || '').toUpperCase();
    const up = (v) => String(v ?? '').toUpperCase();
    return txs
      .map(tx => {
        const ts = new Date(tx.datum || tx.date).getTime();
        if (!Number.isFinite(ts)) return null;
        const coin = up(tx.coin ?? tx.sym);
        if (tx.typ === 'kauf' && coin === sym) return { ts, type: 'buy' };
        if (tx.typ === 'verkauf' && coin === sym) return { ts, type: 'sell' };
        if (tx.typ === 'swap') {
          if (up(tx.coinOut ?? tx.symOut) === sym) return { ts, type: 'sell' };
          if (up(tx.coinIn ?? tx.symIn) === sym) return { ts, type: 'buy' };
        }
        return null;
      })
      .filter(Boolean);
  }, [txs, asset.sym]);

  return (
    <div>
      <div style={{ padding: '0 18px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontFamily: F.display, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: T.text }}>
          {t('course')}
        </span>
        <div style={{ display: 'flex', gap: 12 }}>
          <span style={{ fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums', fontSize: 11.5, color: T.faint || T.mute }}>H {prices.length ? fmt(high, locale) : '–'}</span>
          <span style={{ fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums', fontSize: 11.5, color: T.faint || T.mute }}>L {prices.length ? fmt(low, locale) : '–'}</span>
        </div>
      </div>
      <div style={{ position: 'relative', height: isWide ? 184 : 170, overflow: 'hidden' }}>
        {error ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 10 }}>
            <span style={{ fontSize: 12, color: T.sub }}>{t('asset_chart_load_failed')}</span>
            <button
              onClick={() => { clearHistoricalCache(asset.coinId, period.days, cgParam); setData(null); setError(false); fetchHistoricalData(asset.coinId, period.days, cgParam).then(setData).catch(() => setError(true)); }}
              {...retryPress.pressHandlers}
              style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${T.line}`, background: T.surface, color: T.text, cursor: 'pointer', fontSize: 12, transform: retryPress.pressTransform, transition: `transform ${DUR.press}ms ${EASE}` }}
            >
              {t('asset_retry')}
            </button>
          </div>
        ) : !data || loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.mute, fontSize: 12 }}>
            {t('asset_chart_loading')}
          </div>
        ) : (
          <BigAreaChart
            w={420} h={isWide ? 184 : 170} color={lineColor}
            grid fillOpacity={T.isDark ? 0.16 : 0.12}
            seed={asset.sym.charCodeAt(0) || 1} values={data} markers={markers}
            formatPrice={(n) => `${fmt(n, locale)} ${symbol}`}
            formatTime={(ts) => {
              const d = new Date(ts);
              return period.days <= 1
                ? d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
                : d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
            }}
          />
        )}
      </div>
      {/* Timeframe-Auswahl mit gleitendem Thumb (wie überall sonst) statt Einzel-Pills. */}
      <div style={{ padding: isWide ? '16px 18px 0' : '14px 18px 0' }}>
        <Segmented
          fluid
          value={period.label}
          onChange={(lbl) => setPeriod(PERIODS.find(p => p.label === lbl) || period)}
          options={PERIODS.map(p => ({ value: p.label, label: p.label }))}
        />
      </div>
    </div>
  );
}
// ── ActivityRow — thin wrapper around shared TxRow (variant="card")
// mit Tap-to-expand → Bearbeiten/Löschen.
function ActivityRow({ tx, marketData, transferMap, derivedValues, onEdit, onDelete, flat }) {
  const { T } = useTheme();
  const { locale } = useCurrency();
  const view = useMemo(
    () => buildTxView(tx, { transferMap, derivedValues, marketData, locale }),
    [tx, transferMap, derivedValues, marketData, locale]
  );
  // Aufklappen + Von/An + Bearbeiten/Löschen liegen jetzt in TxRow selbst (EINE
  // Sprache mit dem Haupt-Feed). isOpen/onToggle entfallen — TxRow verwaltet es.
  return (
    <TxRow
      variant="card"
      view={view}
      marketData={marketData}
      rowRadius={16}
      rowShadow={T.cardShadow}
      flat={flat}
      expandable
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );
}
// ── Tx list (WalletRow-Design + tap-to-expand) ─────────────────────
function TransactionList({ txs, sym, marketData, onEdit, onDelete, flat }) {
  const { T } = useTheme();
  const { t } = useT();
  const [expandedId, setExpandedId] = useState(null);
  const [showDust, setShowDust] = useState(false);
  const transferMap = useMemo(() => buildTransferMap(txs), [txs]);
  // Historische EUR-Marktwerte für externe Transfers ohne erfassten Preis.
  // Display ruft convert() drauf, daher hier fest in EUR laden.
  const derivedValues = useHistoricalPrices(txs, { currency: 'eur' });

  const sorted = useMemo(() => {
    // 1) Gebuehr/Fiat-Transfers ausblenden
    const filtered = txs.filter(tx => !adIsNonAssetTransfer(tx) && !adIsInternalFeeTx(tx));
    // 2) Pro Tag + Coin-Paar verdichten (Binance-Sekunden-Splits → eine Karte).
    //    Identisch zur PDF-Verdichtung damit Asset-Detail == PDF == WalletScreen.
    const merged = mergeTxsByDay(filtered);
    // 3) Chronologisch absteigend für die Liste
    return merged.slice().sort((a, b) => {
      const da = a.effectiveTimestamp?.slice(0, 10) || a.datum || '';
      const db = b.effectiveTimestamp?.slice(0, 10) || b.datum || '';
      if (da !== db) return db.localeCompare(da);
      const ta = a.effectiveTimestamp ? a.effectiveTimestamp.slice(11, 19) : (a.zeit || '00:00:00');
      const tb = b.effectiveTimestamp ? b.effectiveTimestamp.slice(11, 19) : (b.zeit || '00:00:00');
      if (ta !== tb) return tb.localeCompare(ta);
      return 0;
    });
  }, [txs]);

  // Scam-Dust (Address-Poisoning) wie im WalletScreen behandeln: Read-Layer-
  // Kandidat (_scamDust) + WERT-Veto (aktueller Preis). Aus der Haupt-Activity
  // ausblenden, einklappbar darunter — Parität zur „Sonstige"-Logik im Wallet.
  const dustIds = useMemo(() => scamDustTxIds(sorted, marketData, derivedValues), [sorted, marketData, derivedValues]);
  const mainTxs = useMemo(
    () => (dustIds.size ? sorted.filter(tx => !dustIds.has(tx._docId || String(tx.id))) : sorted),
    [sorted, dustIds]
  );
  const dustTxs = useMemo(
    () => (dustIds.size ? sorted.filter(tx => dustIds.has(tx._docId || String(tx.id))) : []),
    [sorted, dustIds]
  );

  const grouped = useMemo(() => buildGroupedChains(mainTxs, transferMap), [mainTxs, transferMap]);

  if (sorted.length === 0) {
    return (
      <div style={{ padding: flat ? '0 14px' : '0 18px' }}>
        <div style={{ textAlign: 'center', padding: '28px 16px', color: T.sub, fontSize: 13, background: flat ? 'transparent' : T.surface, borderRadius: flat ? 0 : 18, border: flat ? 'none' : `1px solid ${T.line}` }}>
          {t('asset_no_tx', { sym })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: flat ? '0 14px 12px' : '0 18px 40px' }}>
      {grouped.map(([dateKey, items]) => (
        <div key={dateKey} style={{ marginBottom: flat ? 2 : 10 }}>
          <div style={{ fontFamily: F.mono, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', color: T.mute, textTransform: 'uppercase', padding: flat ? '14px 6px 6px' : '6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
            {formatDateHeader(dateKey)}
            <div style={{ flex: 1, height: 1, background: T.line }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: flat ? 0 : 8 }}>
            {items.map((item) => {
              if (item.type === 'chain') {
                const [txA, txB] = item.txs;
                const idA = txA._docId || String(txA.id);
                return (
                  <TransferChainCard
                    key={`chain-${idA}`}
                    txA={txA}
                    txB={txB}
                    marketData={marketData}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    flat={flat}
                  />
                );
              }
              const tx = item.txs[0];
              const id = tx._docId || String(tx.id);
              // Einzelner Krypto-Wallet-Transfer → aufklappbare Transfer-Karte
              // (identisch zu den gematchten Paaren); Fiat + übrige Typen bleiben
              // die normale, tap-to-expand ActivityRow.
              if (isCryptoWalletTransfer(tx, transferMap)) {
                return (
                  <TransferChainCard
                    key={`xfer-${id}`}
                    txA={tx}
                    txB={null}
                    marketData={marketData}
                    transferMap={transferMap}
                    derivedValues={derivedValues}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    flat={flat}
                  />
                );
              }
              return (
                <ActivityRow
                  key={id}
                  tx={tx}
                  marketData={marketData}
                  transferMap={transferMap}
                  derivedValues={derivedValues}
                  isOpen={expandedId === id}
                  onToggle={() => setExpandedId(expandedId === id ? null : id)}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  flat={flat}
                />
              );
            })}
          </div>
        </div>
      ))}

      {dustTxs.length > 0 && (
        <div style={{ marginTop: flat ? 4 : 14 }}>
          <button
            onClick={() => setShowDust(s => !s)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: flat ? '14px 6px 6px' : '6px 0',
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: F.mono, fontSize: 10.5, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase', color: T.mute,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <span>{t('asset_dust_hidden', { n: dustTxs.length })}</span>
            <div style={{ flex: 1, height: 1, background: T.line }} />
            <span style={{
              display: 'inline-flex',
              transform: showDust ? 'rotate(90deg)' : 'none',
              transition: `transform ${DUR.base}ms ease`,
            }}>
              <Icon d={Icons.chevR} size={12} stroke="currentColor" sw={2.4} />
            </span>
          </button>
          {showDust && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: flat ? 0 : 8, marginTop: flat ? 0 : 6, opacity: 0.55 }}>
              {dustTxs.map((tx) => {
                const id = tx._docId || String(tx.id);
                return (
                  <ActivityRow
                    key={id}
                    tx={tx}
                    marketData={marketData}
                    transferMap={transferMap}
                    derivedValues={derivedValues}
                    isOpen={expandedId === id}
                    onToggle={() => setExpandedId(expandedId === id ? null : id)}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    flat={flat}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────
export default function AssetDetailScreen({ asset, txs, marketData, onEdit, onDelete, onOpenPricing, onOpenAlert, onBack }) {
  const { T } = useTheme();
  const { convert, symbol, locale, cgParam } = useCurrency();
  const { country } = useProfile();
  const { isPremium } = useUserPlan();
  const { t } = useT();
  const { isWide } = useBreakpoint();
  const reduceMotion = useReducedMotion();
  const isDE = country === 'DE';
  // Haltefrist-Timeline (Smart Check) ist DE-spezifisch — für Länder ohne
  // Frist-Konzept (AT/übrige) ausblenden.
  const showSmartCheck = rulesFor(country).showSmartCheck;
  const [showExport, setShowExport] = useState(false);
  const [analyseOpen, setAnalyseOpen] = useState(false);
  const backPress = usePress({ scale: 0.92 });
  const handleExportPick = async (format) => {
    setShowExport(false);
    if (format !== 'pdf' && format !== 'xlsx') return;
    try {
      // Kursverlauf nur fuer PDF holen (Excel hat keinen Chart). Fehlertolerant:
      // schlaegt der Fetch fehl, exportiert das PDF einfach ohne Kurschart.
      let priceSeries;
      if (format === 'pdf' && asset.coinId) {
        try {
          const hist = await fetchHistoricalData(asset.coinId, 365, cgParam);
          if (Array.isArray(hist) && hist.length >= 2) {
            priceSeries = hist.map(p => ({ date: p[0], value: p[1] }));
          }
        } catch { /* Chart optional */ }
      }
      await exportAssetReport({ format, asset, txs, marketData, isDE, country, priceSeries });
    } catch { /* export errors are silent — sheet already closed */ }
  };

  const stats = useMemo(() => calcAssetStats(asset.sym, txs), [asset.sym, txs]);
  // Fallback auf die beim Aufruf mitgegebenen Werte (Top Mover / Märkte) —
  // wichtig für Markt-Coins, die nicht im Portfolio-marketData liegen (sonst 0).
  const price = (marketData?.[asset.coinId]?.price ?? asset.price) ?? 0;
  const chg = (marketData?.[asset.coinId]?.chg ?? asset.chg) ?? 0;
  const currentValue = stats.menge * price;
  // Robuste Investment-Berechnung: Σ aller Käufe minus FIFO-realisierte Anschaffung.
  // Vorteil ggü. stats.invested (das den Lot-Pool nutzt): bei ungematchten Transfers
  // oder Lots ohne sauberen Pegging-Preis bleibt remainingInvested stabil.
  const fifoResults = useMemo(() => fifo(txs), [txs]);
  const investment = useMemo(() => calcAssetInvestment(asset.sym, txs, fifoResults), [asset.sym, txs, fifoResults]);
  const hasPosition = stats.menge > 1e-6;
  // Haltefrist-Lots ohne Scam-Dust bauen — derselbe Filter wie in der Activity-
  // Liste, damit die Timeline (TaxTimeline) keine Address-Poisoning-Tranchen
  // (z.B. 0.000001 XRP) mehr zeigt. Echtes Staking (HBAR) bleibt, da es nie als
  // _scamDust geflaggt wird.
  const lotTxs = useMemo(() => dropScamDustTxs(txs, marketData), [txs, marketData]);
  const taxLots = useMemo(() => (hasPosition ? buildTaxLots(asset.sym, lotTxs) : []), [asset.sym, lotTxs, hasPosition]);
  // Hinweis nur dann zeigen wenn ECHTE Daten-Lücken existieren — d. h. eine
  // Erwerbs-TX (Kauf/Einzahlung-mit-preis/Swap-IN) für dieses Asset hat KEIN
  // exchange-Feld. Wenn alle TXs sauber gepflegt sind und FIFO trotzdem wallet-
  // übergreifend rechnen musste (z. B. weil Coins zwischen Wallets transferiert
  // wurden), ist das KEIN Daten-Problem — daher kein Hinweis.
  const hasIncompleteExchangeData = useMemo(() => {
    const sym = String(asset.sym).toUpperCase();
    return txs.some(tx => {
      const coinSym = String(tx.coin ?? tx.sym ?? '').toUpperCase();
      const coinInSym = String(tx.coinIn ?? tx.symIn ?? '').toUpperCase();
      const isErwerb =
        (tx.typ === 'kauf' && coinSym === sym && Number(tx.preis) > 0) ||
        (tx.typ === 'einzahlung' && coinSym === sym && Number(tx.preis) > 0) ||
        (tx.typ === 'swap' && coinInSym === sym);
      if (!isErwerb) return false;
      const loc = String(tx.exchange || tx.platform || '').trim();
      return !loc;
    });
  }, [txs, asset.sym]);


  // ── Content-Blöcke (geteilt zwischen Mobile-Stack und Desktop-Split) ──
  // Klartext-Delta statt Prozent-Badge: „Heute X % weniger/mehr als gestern".
  const chgNum = Number(chg);
  const hasChg = Number.isFinite(chgNum) && chgNum !== 0;
  const chgDown = chgNum < 0;
  const chgPlain = hasChg ? (
    <>
      {t('asset_chg_pre')}{' '}
      <b style={{ color: chgDown ? (T.loss || T.red) : (T.gain || T.green), fontWeight: 700 }}>
        {fmt(Math.abs(chgNum), locale)} % {chgDown ? t('asset_chg_less') : t('asset_chg_more')}
      </b>{' '}
      {t('asset_chg_post')}
    </>
  ) : t('current_price');

  // Calm-Kopf: flach (kein Karten-Container, kein schwarzer Editorial-Streifen,
  // kein „01 — POSITION"-Caps-Kicker). Coin + Name + Symbol, Preis-Hero, Klartext.
  const overviewCard = (
    <div style={{ padding: isWide ? 0 : '0 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: isWide ? 16 : 14 }}>
        {/* Zurück — Pfeil-only, inline neben dem Coin-Logo (nur Desktop; Mobile
            nutzt den TopBar-Back). Spart die separate Zurück-Zeile → kein Scroll. */}
        {isWide && onBack && (
          <button
            onClick={onBack}
            aria-label={t('back')}
            title={t('back')}
            {...backPress.pressHandlers}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 38, height: 38, flexShrink: 0,
              background: T.surfaceHi || T.surface,
              border: `1px solid ${T.line}`, borderRadius: 999,
              color: T.sub, cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              transform: backPress.pressTransform,
              transition: `color ${DUR.fast}ms ease, background ${DUR.fast}ms ease, transform ${DUR.press}ms ${EASE}`,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.background = T.itemHover; }}
            onMouseLeave={e => { e.currentTarget.style.color = T.sub; e.currentTarget.style.background = T.surfaceHi || T.surface; }}
          >
            <Icon d={Icons.back} size={17} stroke="currentColor" sw={2.4} />
          </button>
        )}
        <CoinImage sym={asset.sym} src={asset.image} marketData={marketData} size={isWide ? 34 : 32} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: F.display, fontSize: isWide ? 21 : 17, fontWeight: 700, letterSpacing: '-0.02em', color: T.text, lineHeight: 1.1 }}>
            {asset.name}
          </div>
          <div style={{ fontFamily: F.family, fontSize: 12.5, color: T.mute, fontWeight: 600 }}>{asset.sym}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums', fontSize: isWide ? 58 : 52, fontWeight: 800, letterSpacing: '-0.045em', lineHeight: 0.94, color: T.text }}>
          {fmt(convert(price), locale)}
        </span>
        <span style={{ fontFamily: F.num || F.mono, fontSize: isWide ? 24 : 21, color: T.sub, fontWeight: 600 }}>{symbol}</span>
      </div>
      <div style={{ fontFamily: F.family, fontSize: 14.5, color: T.sub, marginTop: 10, letterSpacing: '-0.005em' }}>
        {chgPlain}
      </div>
    </div>
  );

  const chartBlock = (
    <>
      <div style={{ marginTop: isWide ? 14 : 24 }}>
        <ChartSection asset={asset} txs={txs} />
      </div>
      {hasPosition && (
        <div style={{ margin: '14px 18px 0', display: 'flex', alignItems: 'center', gap: 18 }}>
          {/* Legende spiegelt die neuen Chart-Marker: Kauf = gefüllter Punkt,
              Verkauf = hohler Ring, beide in den sanften Tönen. */}
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, color: T.sub, fontWeight: 500, letterSpacing: '-0.01em' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.gain || T.green, flexShrink: 0 }} />
            {t('buy')}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, color: T.sub, fontWeight: 500, letterSpacing: '-0.01em' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'transparent', border: `1.6px solid ${T.loss || T.red}`, flexShrink: 0 }} />
            {t('sell')}
          </span>
        </div>
      )}
    </>
  );

  const exchangeHint = hasIncompleteExchangeData && hasPosition && (
    <div style={{ margin: '14px 18px 0', fontSize: 11.5, color: T.mute, fontFamily: F.family, letterSpacing: '-0.005em', lineHeight: 1.5 }}>
      <span style={{ color: T.sub, fontWeight: 600 }}>{t('asset_hint_label')} · </span>
      {t('asset_exchange_hint')}
    </div>
  );

  const taxBlock = hasPosition && showSmartCheck && taxLots.length > 0 && (
    <>
      <SectionDivider num="02" label={t('tax_status')} />
      <TaxTimeline sym={asset.sym} lots={taxLots} />
    </>
  );

  // Desktop-Aktionsleiste — Analyse / Alarm / Export kompakt & weit oben,
  // damit rechts nichts gescrollt werden muss. Analyse ist der primäre CTA.
  const ActionTile = ({ icon, label, onClick, primary, children }) => {
    const [hov, setHov] = useState(false);
    const { pressTransform, pressHandlers } = usePress({});
    return (
      <button
        onClick={onClick}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        {...pressHandlers}
        style={{
          flex: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '14px 10px',
          borderRadius: mr(13),
          // Calm: primär = ruhiger Ton-Button (ink-Fill, kein rotes Füllen),
          // sekundär = neutraler Outline-Button. Rot lebt nur im Marken-Punkt.
          border: primary ? `1px solid ${T.text}` : `1px solid ${T.line}`,
          background: primary
            ? T.text
            : (hov ? T.itemHover : 'transparent'),
          color: primary ? T.bg : (hov ? T.text : T.sub),
          fontFamily: F.family, fontSize: 13, fontWeight: 700,
          letterSpacing: '-0.01em', cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          transform: pressTransform,
          transition: `background ${DUR.fast}ms ease, color ${DUR.fast}ms ease, border-color ${DUR.fast}ms ease, transform ${DUR.press}ms ${EASE}`,
        }}
      >
        {children || (
          <>
            <Icon d={icon} size={16} stroke="currentColor" sw={2.2} />
            {label}
          </>
        )}
      </button>
    );
  };
  const desktopActionBar = (
    <div style={{ display: 'flex', gap: 9, marginTop: 18 }}>
      {hasPosition && (
        <ActionTile primary onClick={() => setAnalyseOpen(true)}>
          <RedWordmark size={15} sub={t('asset_analyse')} color={T.bg} />
        </ActionTile>
      )}
      <ActionTile icon={Icons.bell} label={t('asset_alarm')} onClick={() => onOpenAlert?.(asset)} />
      <ActionTile icon={Icons.export} label={t('asset_export_label')} onClick={() => setShowExport(true)} />
    </div>
  );

  const txBlock = hasPosition
    ? <TransactionList txs={txs} sym={asset.sym} marketData={marketData} onEdit={onEdit} onDelete={onDelete} flat={isWide} />
    : (
      <div style={{ padding: '24px 18px', color: T.sub, fontSize: 12, textAlign: 'center' }}>
        {t('asset_no_position', { sym: asset.sym })}
      </div>
    );

  const modals = (
    <>
      {analyseOpen && (
        <AssetAnalyseSheet
          open={analyseOpen}
          onClose={() => setAnalyseOpen(false)}
          asset={asset}
          stats={stats}
          txs={txs}
          fifoResults={fifoResults}
          currentValue={currentValue}
          marketData={marketData}
          investment={investment}
        />
      )}
      {showExport && (
        <ExportSheet
          open={showExport}
          onClose={() => setShowExport(false)}
          scope="asset"
          asset={asset}
          isPremium={isPremium}
          onPick={handleExportPick}
          onLocked={() => { setShowExport(false); onOpenPricing && onOpenPricing(); }}
        />
      )}
    </>
  );

  // ── Desktop: Split-Layout (Transaktionen links, Übersichts-Monitor rechts) ──
  if (isWide) {
    const EASE = [0.23, 1, 0.32, 1];
    const enterX = reduceMotion ? 0 : 40;
    return (
      <>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(440px, 1.06fr) minmax(380px, 1fr)',
          gap: 28,
          height: 'calc(100vh - 158px)',
          alignItems: 'stretch',
        }}>
          {/* LINKS — Übersichts-Monitor. Alles auf einen Blick, kein Scroll:
              Position+Aktionen, Chart, Haltefrist-Monitor. */}
          <motion.section
            initial={{ opacity: 0, x: -enterX }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: DUR.entrance / 1000, ease: EASE }}
            className="hide-scrollbar"
            style={{ minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
          >
            {overviewCard}
            {desktopActionBar}
            {chartBlock}
            {taxBlock}
          </motion.section>

          {/* RECHTS — Transaktionen, flach & ohne Hintergrund, oben bündig mit
              der weißen Kante des Übersichts-Monitors. Eigener Scroll-Container. */}
          <motion.section
            initial={{ opacity: 0, x: enterX }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: DUR.entrance / 1000, ease: EASE, delay: reduceMotion ? 0 : 0.04 }}
            className="hide-scrollbar"
            style={{
              minHeight: 0, minWidth: 0,
              overflowY: 'auto',
            }}
          >
            {txBlock}
          </motion.section>
        </div>
        {modals}
      </>
    );
  }

  // ── Mobile: Einspaltiger Stack ──
  return (
    <div style={{ paddingBottom: 100, paddingTop: 10 }}>
      {/* Kein „01 Übersicht"-Kicker mehr — der Coin-Name ist die Headline. */}
      {overviewCard}
      {/* Aktionsbereich wie in der Webapp: red.Analyse / Alarm / Export als
          Tile-Reihe direkt unter der Position-Card (statt separater Analyse-Zeile
          + „03 AKTION"-Block). Mobil mit 18px-Seitenpadding ausgerichtet. */}
      <div style={{ padding: '0 18px' }}>{desktopActionBar}</div>
      {chartBlock}
      {exchangeHint}
      {taxBlock}
      {hasPosition && <SectionDivider num="04" label={t('tx_history')} />}
      {txBlock}
      {modals}
    </div>
  );
}
